import styled from 'styled-components'
import { useTranslation } from 'utils/useTranslation'
import { Infobox, Typography, Icon } from 'components'
import { padding, color, shadow, borderRadius, margin } from '@evrekadev/evreka-ui-components'
import { UilMapMarker, UilTimes } from '@iconscout/react-unicons'
import { useMapContext } from 'components/MapV2/MapProvider'
import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

const SelectedMarkers: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const { t } = useTranslation('components.map')
  const containerRef = useRef<HTMLDivElement>(null)
  const currentMap = useMap()

  const {
    selectedMarkers: markers,
    onMarkerSelect: onMarkerRemove,
    showBulkSelection,
  } = useMapContext()

  //disable mouse events inside the search and menu
  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableScrollPropagation(containerRef.current)
      L.DomEvent.disableClickPropagation(containerRef.current)
      if (containerRef.current) {
        if (containerRef.current) {
          containerRef.current.addEventListener('mouseenter', () => {
            currentMap.dragging.disable()
            currentMap.doubleClickZoom.disable()
            currentMap.scrollWheelZoom.disable()
          })
          containerRef.current.addEventListener('mouseleave', () => {
            currentMap.dragging.enable()
            currentMap.doubleClickZoom.enable()
            currentMap.scrollWheelZoom.enable()
          })
        }
      }
    }
  }, [containerRef])

  if (!isActive || !showBulkSelection) return null

  return (
    <Container style={containerStyle} ref={containerRef}>
      <Typography fontSize="text_sm" fontWeight="semibold">
        {t('selected_markers')} ({markers?.length})
      </Typography>
      <MarkersContainer>
        {!markers?.length && (
          <div className="info-box">
            <Infobox text={t('draw_an_area')} hideClose showIcon={false} variant="warning" />
          </div>
        )}
        {markers?.map((marker, idx) => {
          return (
            <div key={marker.id || idx} className="marker-item">
              <div className="title">
                <Icon icon={UilMapMarker} color="green500" />
                <Typography>{marker.marker.title || marker.tooltip?.text}</Typography>
              </div>
              <div onClick={() => onMarkerRemove?.(marker)}>
                <TimesIconContainer className="remove_marker" icon={UilTimes} color="darkblue500" />
              </div>
            </div>
          )
        })}
      </MarkersContainer>
    </Container>
  )
}

const Container = styled.div`
  ${padding([12])}
  ${shadow('xs')}
  ${borderRadius(12)}
  position: absolute;
  z-index: 1000;
  top: 10px;
  width: 224px;
  height: 188px;
  background: ${color('white')};
  border-bottom: 1px solid ${color('grey300')};

  p {
    margin-left: 0 !important;
  }
`

const MarkersContainer = styled.div`
  ${margin({ t: 4 })}
  height: 136px;
  overflow-y: auto;

  & > .marker-item {
    ${padding([2])}
    ${margin({ b: 4 })}
    display: flex;
    justify-content: space-between;

    .title {
      display: flex;
      flex-shrink: 0;

      p {
        width: 141px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .remove_marker {
      display: none;
    }

    &:hover {
      ${borderRadius(4)}
      background: ${color('grey50')};

      .remove_marker {
        display: block;
      }
    }
  }
`

const TimesIconContainer = styled(Icon)`
  cursor: pointer;
`

const containerStyle: React.CSSProperties = {
  textAlign: 'left',
  right: 10,
}

export default SelectedMarkers
